import { lazy, Suspense, type ComponentProps } from 'react'
import type { FurniturePreview as FurniturePreviewNow } from '@/components/FurniturePreview'
import type { FurnitureGroupPreview as FurnitureGroupPreviewNow } from '@/components/FurnitureGroupPreview'
import type { FurnitureEditor as FurnitureEditorNow } from '@/components/FurnitureEditor'
import type { GroupEditor as GroupEditorNow } from '@/components/GroupEditor'
import type { PublicPlan as PublicPlanNow } from '@/floorplan/PublicPlan'
import { LoadingState } from '@/components/ui/states'

/**
 * The drawing components, loaded the first time one is shown.
 *
 * Each is a small Konva stage, and Konva (with react-konva) was the biggest
 * single library in the one script every page waits for, sign-in included:
 * pages that only list furniture pulled it in through these. Importing them
 * from here instead keeps the library out of that first download. The floor-plan
 * editors import the originals directly, because they already load on their own
 * (`router.tsx`).
 *
 * The types come from the originals (`import type`, which loads nothing), so
 * each stand-in takes exactly the props its original does.
 */

const FurniturePreviewLater = lazy(async () => ({ default: (await import('@/components/FurniturePreview')).FurniturePreview }))
const FurnitureGroupPreviewLater = lazy(async () => ({
  default: (await import('@/components/FurnitureGroupPreview')).FurnitureGroupPreview,
}))
const FurnitureEditorLater = lazy(async () => ({ default: (await import('@/components/FurnitureEditor')).FurnitureEditor }))
const GroupEditorLater = lazy(async () => ({ default: (await import('@/components/GroupEditor')).GroupEditor }))
const PublicPlanLater = lazy(async () => ({ default: (await import('@/floorplan/PublicPlan')).PublicPlan }))

/** A tile's drawing. Nothing in its place while it loads: the tile's grid is already there. */
export function FurniturePreview(props: ComponentProps<typeof FurniturePreviewNow>) {
  return (
    <Suspense fallback={null}>
      <FurniturePreviewLater {...props} />
    </Suspense>
  )
}

/** A furniture group's drawing; the list row shows its name meanwhile. */
export function FurnitureGroupPreview(props: ComponentProps<typeof FurnitureGroupPreviewNow>) {
  return (
    <Suspense fallback={null}>
      <FurnitureGroupPreviewLater {...props} />
    </Suspense>
  )
}

/** Drawing a furniture piece. */
export function FurnitureEditor(props: ComponentProps<typeof FurnitureEditorNow>) {
  return (
    <Suspense fallback={<LoadingState label="Loading the drawing…" />}>
      <FurnitureEditorLater {...props} />
    </Suspense>
  )
}

/** Arranging a furniture group. */
export function GroupEditor(props: ComponentProps<typeof GroupEditorNow>) {
  return (
    <Suspense fallback={<LoadingState label="Loading the drawing…" />}>
      <GroupEditorLater {...props} />
    </Suspense>
  )
}

/** A plan as a public page shows it (the event's share link, a vendor's event). */
export function PublicPlan(props: ComponentProps<typeof PublicPlanNow>) {
  return (
    <Suspense fallback={<LoadingState label="Loading the plan…" />}>
      <PublicPlanLater {...props} />
    </Suspense>
  )
}
